<template>
  <form @submit.prevent="login" class="form">
    <ui-input
        :type="'email'"
        :placeholder="t('fields.email')"
        :rules="[isEmail]"
        v-model="email"
    />
    <ui-input
        :type="'password'"
        :placeholder="t('fields.password')"
        :rules="[isPasswordValid]"
        v-model="password"
    />
    <p class="form__text">{{ t('auth.login.text1') }} <router-link :to="translations.i18nRoute({ name: 'forgot' })">{{ t('buttons.reset') }}</router-link></p>
    <p class="form__text">{{ t('auth.login.text2') }} <router-link :to="translations.i18nRoute({ name: 'register' })">{{ t('buttons.signUp') }}</router-link></p>
    <ui-checkbox
        v-model="isRemember"
        :id="1"
    >
      {{ t('checkboxes.remember') }}
    </ui-checkbox>
    <ui-button
        :type="'submit'"
        class="form__button"
        :isDisabled="!isFormValid"
        :isLoading="isLoading"
    >
      {{ t('buttons.signIn') }}
      <svg xmlns="http://www.w3.org/2000/svg" width="27" height="28" viewBox="0 0 27 28" fill="none">
        <path d="M9.5625 6.125L17.4375 14L9.5625 21.875" stroke="#008ECC" stroke-width="2.25" stroke-linecap="round" stroke-linejoin="round"/>
      </svg>
    </ui-button>
  </form>
</template>

<script setup>
import {isEmail, isPasswordValid, required} from "@/core/rules/textFieldRules.js";
import UiInput from "@/components/ui/ui-input.vue";
import {computed, ref} from "vue";
import {ElNotification} from "element-plus";
import {Auth} from "@/core/api/auth.js";
import {Wishlist} from "@/core/api/wishlist.js";
import {Orders} from "@/core/api/orders.js";
import {useWishlistStore} from "@/stores/wishlist.js";
import {useCartStore} from "@/stores/cart.js";
import {useAuthStore} from "@/stores/auth.js";
import {useI18n} from "vue-i18n";
import {useRouter} from "vue-router";
import translations from "@/core/helpers/translations.js";
import UiCheckbox from "@/components/ui/ui-checkbox.vue";
import UiButton from "@/components/ui/ui-button.vue";

const {t} = useI18n()
const router = useRouter()
const authStore = useAuthStore()
const cartStore = useCartStore()
const wishlistStore = useWishlistStore()

const email = ref('')
const password = ref('')
const isRemember = ref(false)

const isLoading = ref(false)

const isFormValid = computed(() => {
  return (
      isEmail(email.value) === true &&
      required(password.value) === true
  )
})

const login = async () => {
  isLoading.value = true

  const res = await Auth.login(email.value, password.value)

  if (!res.errors) {
    authStore.setUser({
      user: res.data.obtainJwtToken.user,
      accessToken: res.data.obtainJwtToken.accessToken
    })

    await router.push({
      name: 'home',
      params: {
        locale: localStorage.getItem('nest-user-locale') || 'en-gb'
      }
    })

    localStorage.setItem('nest-refresh', res.data.obtainJwtToken.refreshToken)

    const currentOrder = await Orders.getOrders("PENDING", res.data.obtainJwtToken.accessToken, res.data.obtainJwtToken.user.email)
    cartStore.setCurrentOrders(currentOrder.data.orders.edges[0].node)

    const wishlistRes = await Wishlist.getWishlist(res.data.obtainJwtToken.accessToken)
    wishlistStore.wishlist = wishlistRes.data.wishlists.edges[0].node
  } else {
    ElNotification({
      message: res.errors[0].message,
      type: 'error'
    })
  }

  isLoading.value = false
}
</script>

<style lang="scss" scoped>
.form {
  width: 400px;
  align-self: center;
  display: flex;
  flex-direction: column;
  gap: 15px;

  &__text {
    color: #222;
    font-weight: 500;
    line-height: 27px;
    letter-spacing: -0.16px;

    & a {
      transition: 0.2s;
      color: #008ECC;
      font-weight: 600;
      line-height: 27px;
      letter-spacing: -0.16px;
      text-decoration: underline;

      @include hover {
        color: #006699;
      }
    }
  }

  &__link {
    transition: 0.2s;
    color: #008ECC;
    font-weight: 600;
    line-height: 27px;
    letter-spacing: -0.16px;
    text-decoration: underline;

    @include hover {
      color: #006699;
    }
  }

  &__button {
    width: fit-content;
    padding-inline: 20px;
    align-self: flex-end;
  }
}

@media (max-width: 600px) {
  .form {
    width: 100%;
  }
}
</style>